import * as React from "react";
import { IconChevron, Button, List, DropdownMenu, Dropdown } from "atoms";
import { useToggle } from "utils/hooks";
import type { ReviewSortOption } from "../types";

const sortOptions: [ReviewSortOption, string][] = [
  ["createdAt desc", "Newest"],
  ["createdAt", "Oldest"],
];

interface ReviewSortProps {
  createHandler: (option: ReviewSortOption) => VoidFunction;
  isModerationPage?: boolean;
}

const useReviewSort = (defaultOption?: ReviewSortOption) => {
  const [activeReviewSort, setActiveReviewSort] =
    React.useState<ReviewSortOption>(defaultOption ?? sortOptions[0][0]);

  const createReviewSortHandler = (option: ReviewSortOption) => () => {
    setActiveReviewSort(option);
  };

  return { activeReviewSort, createReviewSortHandler };
};

function ReviewSort({
  createHandler,
  isModerationPage = false,
}: ReviewSortProps) {
  const [isOpen, toggle, setIsOpen] = useToggle(false);

  /**
   * When focus leaves the parent element of our dropdown menus, reset to close them.
   * NOTE: we're running this event on the capture phase to prevent child elements firing this same event handler.
   */
  const onBlurCapture: React.FocusEventHandler = (event) => {
    // Event will travel from root element to this element, but we want to stop it from continuing to this element's children
    // event.stopPropagation();
    // Current target is the element we attached the handler to, and the related target is the newly focused element. If the new
    // element is not a descendent of the current target, then we are outside of the nav element.
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsOpen(false);
    }
  };
  // Closes sort options dropdown when focus moves off of the sort toggle button
  const setOptionHandler = (option: ReviewSortOption) => () => {
    setIsOpen(false);
    createHandler(option)();
  };

  return (
    <Dropdown onBlur={onBlurCapture}>
      <Button
        onClick={toggle}
        aria-expanded={isOpen}
        variant="secondary"
        size="xs"
        aria-controls="sort-menu"
      >
        Sort <IconChevron size=".75em" rotate={isOpen ? 0 : 180} />
      </Button>
      <DropdownMenu isOpen={isOpen} id="sort-menu">
        <List type="blank">
          {sortOptions
            .filter(
              (entry) => !(isModerationPage && entry[0] === sortOptions[0][0])
            )
            .map(([option, label]) => (
              <li key={option}>
                <Button variant="blank" onClick={setOptionHandler(option)}>
                  {label}
                </Button>
              </li>
            ))}
        </List>
      </DropdownMenu>
    </Dropdown>
  );
}

export { ReviewSort, useReviewSort };
